import React from 'react';
import {
  Link,
  useLoaderData,
  useNavigation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useShallow } from 'zustand/shallow';
import { Loader } from '@components';
import { useApplicationStore } from '@data';
import {
  ArrowRightIcon,
  Button,
  Description,
  Field,
  Label,
  Paragraph,
} from '@quickmd-llc/design-system';
import { useIsLoggedIn } from '@hooks/useIsLoggedIn';
import { Events, tracking } from '@lib/events';
import { getDefaultVisitType } from '@pages/ServicesPage/utils';
import {
  DRUG_SCREEN_ROUTE_PATHNAME,
  INTAKE_QUIZ_ROUTE_PATHNAME,
  SCHEDULE_APPOINTMENT_TIME_PREFERENCE_ROUTE_PATHNAME,
} from '@router/routes/routePathnames';
import { getVisitTypeListUrl } from '@router/utils';
import { ServiceType, VisitType } from '@types';
import AccountBlockedAlert from '@pages/SchedulePage/components/AccountBlockedAlert';
import { useMht } from '@pages/SchedulePage/hooks/useMht';
import {
  serviceIsAddictionTreatment,
  serviceIsMht,
} from '@pages/SchedulePage/utils';

interface ListProps {
  selectFromVisitTypes: boolean;
  serviceType: ServiceType;
  isServiceTypeList: boolean;
  visitType: VisitType;
}

type Props = { getVisitTypeUrl: (visitTypeId: string) => string };

const MHT_RESUBSCRIPTION_DESCRIPTION = `$79/month: Includes 1 monthly visit and prescription management; additional visits for $49.`;

export default function AvailableServices({
  getVisitTypeUrl,
}: Props): JSX.Element | null {
  const { state } = useNavigation();
  const serviceTypes = useLoaderData() as ServiceType[] | null;
  const { serviceTypeId } = useParams();
  const [searchParams] = useSearchParams();
  const [isBlockedAlertOpen, setIsBlockedAlertOpen] = React.useState(false);
  const { patientsMostRecentMhtSubscriptionIsCancelled } = useMht();
  const {
    auth,
    experimentCuratedAppointmentSlotsEnabled,
    hasCuratedAppointmentSlotsFlag,
    experimentMatQuestionsEnabled,
    hasMatQuestionsFlag,
  } = useApplicationStore(
    useShallow((store) => ({
      auth: store.auth,
      experimentCuratedAppointmentSlotsEnabled:
        store.flags.experimentCuratedAppointmentSlotsEnabled,
      hasCuratedAppointmentSlotsFlag:
        store.flags.experimentCuratedAppointmentSlots,
      experimentMatQuestionsEnabled:
        store.flags.experimentMatQuestionsEnabled,
      hasMatQuestionsFlag: store.flags.experimentMatQuestions,
    })),
  );
  const { isLoggedIn } = useIsLoggedIn();

  let visitTypes: VisitType[] = [];
  let selectedServiceType: ServiceType | undefined;

  if (state === 'loading') {
    return <Loader />;
  }

  if (!serviceTypes) {
    return null;
  }

  if (serviceTypes.length === 0) {
    return (
      <Paragraph size="md" className="text-center">
        Sorry, we don’t offer any services in your state.
      </Paragraph>
    );
  }

  if (serviceTypeId) {
    selectedServiceType = serviceTypes.find(
      (serviceType) => serviceType.serviceTypeId === serviceTypeId,
    );
    visitTypes = selectedServiceType?.visitTypes || [];
  }

  function handleClick(serviceType: ServiceType) {
    tracking.setBaseProperty({ serviceTypeId: serviceType.serviceTypeId });

    tracking.trackEvent(Events.SELECT_SERVICE, {
      ServiceType: serviceType.name,
    });

    // MAT experiment enrollment is fired in the destination loaders/page
    // (intakeQuizLoader's page on createSession success, scheduleLoader,
    // appointmentTimePreferenceLoader) rather than here, so users who deep-
    // link to /schedule/<MOUD> or /intake-quiz/<MOUD> without going through
    // this click are still counted in the cohort. See
    // src/lib/experiments/matQuestions.ts.
  }

  function getListView({
    serviceType,
    selectFromVisitTypes,
    isServiceTypeList,
    visitType,
  }: ListProps) {
    const item: ServiceType | VisitType = isServiceTypeList
      ? serviceType
      : visitType;

    const description =
      'shortDescription' in item ? item.shortDescription : item.description;
    const key = 'serviceTypeId' in item ? item.serviceTypeId : item.visitTypeId;
    const name = item.name;

    const inPersonVisitVisitTypeId = serviceType.visitTypes.find(
      (x) => x.isInPerson,
    )?.visitTypeId;

    const normalLink = selectFromVisitTypes
      ? getVisitTypeListUrl(
          serviceType.serviceTypeId,
          searchParams.get('state') || '',
        )
      : // if the service type is returning that an in person visit is required, find the visit type for the in person
        // visit, and send them there if it exits. otherwise key off its nullishness to show an on hold/error popup
        serviceType.isInPersonVisitRequired
        ? inPersonVisitVisitTypeId
          ? getVisitTypeUrl(inPersonVisitVisitTypeId)
          : null
        : getVisitTypeUrl(visitType.visitTypeId);

    let finalLink = normalLink;

    const singleVisitAndDrugScreen =
      isServiceTypeList &&
      serviceType.visitTypes.length === 1 &&
      serviceType.visitTypes[0].isDrugScreenRequired;

    const thisVisitIsDrugScreen =
      !isServiceTypeList &&
      (visitType.isDrugScreenRequired || visitType.isDrugScreen);

    if (singleVisitAndDrugScreen || thisVisitIsDrugScreen) {
      finalLink = `${DRUG_SCREEN_ROUTE_PATHNAME}/${visitType.visitTypeId}`;
    }

    if (
      !isLoggedIn &&
      experimentCuratedAppointmentSlotsEnabled &&
      hasCuratedAppointmentSlotsFlag
    ) {
      finalLink = `${SCHEDULE_APPOINTMENT_TIME_PREFERENCE_ROUTE_PATHNAME}/${visitType.visitTypeId}`;
    }

    // MAT experiment overrides curated slots routing for addiction-treatment
    // services: treatment-arm patients go through the MAT question flow before
    // seeing availability. Must come after the curated-slots block so it wins.
    if (
      !isLoggedIn &&
      experimentMatQuestionsEnabled &&
      hasMatQuestionsFlag &&
      serviceIsAddictionTreatment(serviceType.serviceTypeId)
    ) {
      finalLink = `${INTAKE_QUIZ_ROUTE_PATHNAME}/${visitType.visitTypeId}`;
    }

    const showItemPrice = !isServiceTypeList && !visitType.isDrugScreen;

    const Wrapper = ({ children }: React.PropsWithChildren) => {
      if (!finalLink) {
        return (
          <button onClick={() => setIsBlockedAlertOpen(true)}>
            {children}
          </button>
        );
      }

      return (
        <Link to={finalLink} onClick={() => handleClick(serviceType)}>
          {children}
        </Link>
      );
    };

    return (
      <div key={key}>
        {isBlockedAlertOpen && (
          <AccountBlockedAlert
            handleClose={() => setIsBlockedAlertOpen(false)}
          />
        )}
        <Wrapper>
          <div
            className="flex items-center justify-between gap-3 break-all rounded-lg border border-shade-10 bg-coastal-blue-10 p-4"
            data-testid={`pw_service-${serviceType.name}`}
          >
            <div className="flex items-center gap-3">
              {isServiceTypeList && (
                <img
                  className="h-20 min-h-20 w-20 min-w-20 animate-pulse rounded-lg bg-gray-100"
                  src={serviceType.imageUrl || ''}
                  alt={serviceType.name}
                  onLoad={({ currentTarget }) => {
                    currentTarget.classList.remove('animate-pulse');
                    currentTarget.classList.remove('bg-gray-100');
                  }}
                />
              )}
              <div>
                <Paragraph className="break-normal font-semibold text-text-dark">
                  {name}
                </Paragraph>
                {showItemPrice && (
                  <Paragraph size="xs" className="break-normal text-text-light">
                    {visitType.price ? `$${visitType.price}` : 'No charge'}
                  </Paragraph>
                )}
                <Paragraph size="xs" className="break-normal text-text-light">
                  {patientsMostRecentMhtSubscriptionIsCancelled &&
                  serviceIsMht(serviceType.serviceTypeId)
                    ? MHT_RESUBSCRIPTION_DESCRIPTION
                    : description}
                </Paragraph>
              </div>
            </div>

            <Button
              priority="primary"
              size="s"
              className="flex-shrink-0"
              isIcon
            >
              <ArrowRightIcon />
            </Button>
          </div>
        </Wrapper>
      </div>
    );
  }

  function getServiceListView() {
    return serviceTypes?.map((serviceType) => {
      const defaultVisit = getDefaultVisitType(serviceType);
      if (!defaultVisit) return null;

      const selectFromVisitTypes = Boolean(
        !serviceTypeId &&
          auth?.accessToken &&
          serviceType.visitTypes.length > 1 &&
          // if in person visit is required, skip the service type list and just send them to booking an in person visit
          !serviceType.isInPersonVisitRequired,
      );

      return getListView({
        isServiceTypeList: true,
        selectFromVisitTypes,
        serviceType,
        visitType: defaultVisit,
      });
    });
  }

  function getVisitListView() {
    return visitTypes.map((visitType) =>
      getListView({
        isServiceTypeList: false,
        selectFromVisitTypes: false,
        serviceType: selectedServiceType!,
        visitType,
      }),
    );
  }

  return (
    <div>
      {serviceTypeId ? (
        <Field className="mb-2">
          <Label>What type of visit do you need to book?</Label>
        </Field>
      ) : (
        <Field className="mb-2">
          <Label>What service are you interested in?</Label>
          <Description>
            You’ll be able to book other services later.
          </Description>
        </Field>
      )}

      <div className="grid gap-2" data-testid="pw_available-services">
        {!serviceTypeId ? getServiceListView() : getVisitListView()}
      </div>
    </div>
  );
}
